import React, { useState, useRef, useEffect } from 'react';
import { GoogleGenerativeAI } from "@google/generative-ai";
import { MessageSquare, X, Send, Loader2, Sparkles, User, Terminal } from 'lucide-react';
import { clsx } from 'clsx';
import { useLocation } from 'react-router-dom';
import { useStore } from '../hooks/useStore.ts';
import { api } from '../services/api.ts';
import { useRole } from '../hooks/useRole.ts';

// Lazy initialization - only create when actually sending a message
let _ai: GoogleGenerativeAI | null = null;
const getAI = (): GoogleGenerativeAI | null => {
  if (!_ai) {
    // Check localStorage first (user setting), then env var
    const storedKey = localStorage.getItem('lc_api_key');
    const apiKey = (storedKey || import.meta.env.VITE_GEMINI_API_KEY || '').trim();

    if (!apiKey) {
      console.warn("AI Chatbot: No API key configured");
      return null;
    }
    _ai = new GoogleGenerativeAI(apiKey);
  }
  return _ai;
};

interface Message {
  role: 'user' | 'model';
  text: string;
}

export const AIChatbot: React.FC = () => {
  const { isOwner } = useRole();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const projectId = pathParts[1] === 'projects' ? Number(pathParts[2]) : null;

  const project = useStore(() => projectId ? api.projects.get(projectId) : Promise.resolve(null), [projectId], 'projects');
  const evm = useStore(() => projectId ? api.evm.list(e => e.projectId === projectId) : Promise.resolve([]), [projectId], 'evm');

  const [messages, setMessages] = useState<Message[]>([
    { role: 'model', text: 'Operational Intelligence Active. I am monitoring the Job Book for margin variances.' }
  ]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Listen for sidebar button click
  useEffect(() => {
    const handleToggle = () => setIsOpen(prev => !prev);
    document.addEventListener('toggle-ai-chatbot', handleToggle);
    return () => document.removeEventListener('toggle-ai-chatbot', handleToggle);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Fetch Knowledge Base for RAG
  const knowledgeBase = useStore(() => api.knowledgeBase.list(), [], 'knowledgeBase');

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);

    try {
      const ai = getAI();
      if (!ai) {
        setMessages(prev => [...prev, {
          role: 'model',
          text: 'AI features are not configured. Please go to Settings > Intelligence to enter your API Key.'
        }]);
        setLoading(false);
        return;
      }

      // Simple RAG: Find relevant docs
      const relevantDocs = knowledgeBase?.filter(doc => {
        const query = userMsg.toLowerCase();
        return (
          doc.title.toLowerCase().includes(query) ||
          doc.tags?.some(tag => query.includes(tag.toLowerCase())) ||
          (doc.location && query.includes(doc.location.toLowerCase()))
        );
      }).slice(0, 3); // Top 3 relevant docs

      let context = "General System Context: All active jobs.";

      // Inject Knowledge Base
      if (relevantDocs && relevantDocs.length > 0) {
        context += `\n\nOFFICIAL REGULATIONS & RULES (${relevantDocs.length} found):\n`;
        relevantDocs.forEach(doc => {
          context += `[Source: ${doc.title} (${doc.location || 'General'})]\n${doc.content}\n\n`;
        });
      }

      if (project) {
        const latestEvm = evm && evm.length > 0 ? evm[evm.length - 1] : null;
        context += `\nProject Context: Project "${project.title}". Contract: $${project.contractAmount}. Status: ${project.status}. Client: ${project.client}. ` +
          (latestEvm ? `Latest Performance: Spent $${latestEvm.actualCost}, Earned $${latestEvm.earnedValue}.` : "");

        // Add Project Documents Context (Open Notebook)
        const docs = await api.documents.list(d => d.projectId === projectId);
        const docsWithText = docs.filter(d => d.textContent && d.textContent.trim().length > 0);

        if (docsWithText.length > 0) {
          context += `\n\n=== PROJECT DOCUMENTS (OPEN NOTEBOOK) ===\n`;
          docsWithText.forEach(doc => {
            // Truncate if necessary to avoid hitting token limits, though Gemini 2.0 has large context
            const textPreview = doc.textContent!.slice(0, 30000); // 30k char limit per doc for now
            context += `\n--- Document: ${doc.fileName} (${doc.fileType}) ---\n${textPreview}\n`;
          });
          context += `\n=== END PROJECT DOCUMENTS ===\n`;
        }
      }

      const model = ai.getGenerativeModel({
        model: 'gemini-2.0-flash-exp',
        systemInstruction: `${context} You are a world-class Project Controls and EVM expert for LeanControls Pro. \n
        If the user asks about regulations, cite the provided 'OFFICIAL REGULATIONS & RULES'. \n
        Focus on protecting the contractor margin. Be direct, professional, and slightly aggressive regarding cost recovery.`
      });

      // Construct history correctly for SDK
      const history = messages.slice(1).map(m => ({
        role: m.role,
        parts: [{ text: m.text }]
      }));

      const chat = model.startChat({
        history: history,
        generationConfig: {
          maxOutputTokens: 1000,
        },
      });

      const result = await chat.sendMessage(userMsg);
      const modelText = result.response.text();

      setMessages(prev => [...prev, { role: 'model', text: modelText }]);
    } catch (error: any) {
      console.error("AI Chatbot error:", error);
      setMessages(prev => [...prev, {
        role: 'model',
        text: `Error: ${error.message || "Connection failed"}. (Key ends with: ...${import.meta.env.VITE_GEMINI_API_KEY?.slice(-4)})`
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate document stats for UI
  const docs = useStore(() => projectId ? api.documents.list(d => d.projectId === projectId) : Promise.resolve([]), [projectId], 'documents');
  const indexedDocsCount = docs ? docs.filter(d => d.textContent && d.textContent.trim().length > 0).length : 0;

  useEffect(() => {
    if (indexedDocsCount > 0 && messages.length === 1) {
      setMessages(prev => [{
        role: 'model',
        text: `Operational Intelligence Active. I have read ${indexedDocsCount} documents from the Job Book and am monitoring margin variances.`
      }]);
    }
  }, [indexedDocsCount]); // Only run when docs are loaded

  // Drag state
  const [position, setPosition] = useState({ x: window.innerWidth - 380, y: window.innerHeight - 560 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  // Handle Dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      // Calculate new position
      let newX = e.clientX - dragStart.current.x;
      let newY = e.clientY - dragStart.current.y;

      // Constraints (Prevent flying off screen)
      const maxX = window.innerWidth - 350; // Panel width
      const maxY = window.innerHeight - 500; // Panel height

      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));

      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  if (!isOwner) return null;

  return (
    <div className="fixed z-[150]" style={{ left: !isOpen ? 'auto' : position.x, top: !isOpen ? 'auto' : position.y, bottom: !isOpen ? 24 : 'auto', right: !isOpen ? 24 : 'auto' }}>
      {!isOpen ? (
        <button
          onClick={() => {
            setIsOpen(true);
            // Reset position to sensible default if it was closed
            setPosition({ x: window.innerWidth - 380, y: window.innerHeight - 560 });
          }}
          className="w-12 h-12 bg-black text-white rounded-2xl flex items-center justify-center shadow-2xl hover:scale-110 active:scale-95 transition-all group overflow-hidden border-2 border-white/20"
          title="Open Project Intelligence"
        >
          <Sparkles className="text-amber-400 animate-pulse" size={20} />
        </button>
      ) : (
        <div className="w-[350px] h-[500px] bg-white rounded-[2.5rem] border-2 border-zinc-900/10 shadow-[0_30px_80px_-10px_rgba(0,0,0,0.3)] flex flex-col overflow-hidden animate-in zoom-in-95 duration-300 backdrop-blur-xl">
          {/* Header - Draggable Handle */}
          <div
            className="p-6 bg-black text-white flex justify-between items-center relative overflow-hidden cursor-move select-none"
            onMouseDown={handleMouseDown}
          >
            <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none"><Terminal size={80} /></div>
            <div className="relative z-10 flex items-center gap-3">
              <div className="p-2 bg-zinc-800 rounded-xl ring-1 ring-zinc-700 shadow-inner"><Sparkles size={16} className="text-amber-400" /></div>
              <div>
                <h3 className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-400">Project Intel</h3>
                <div className="text-[9px] font-bold text-white tracking-wide truncate max-w-[150px]">
                  {project ? project.title : 'System Nominal'}
                </div>
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-zinc-800 text-zinc-500 hover:text-white transition-all relative z-10"
            >
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar bg-zinc-50/50">
            {messages.map((m, i) => (
              <div key={i} className={clsx("flex flex-col animate-in fade-in slide-in-from-bottom-2", m.role === 'user' ? "items-end" : "items-start")}>
                <div className={clsx(
                  "max-w-[90%] p-4 rounded-2xl text-[11px] font-bold leading-relaxed shadow-sm",
                  m.role === 'user' ? "bg-black text-white rounded-tr-none" : "bg-white border border-zinc-200 text-zinc-700 rounded-tl-none"
                )}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start animate-in fade-in">
                <div className="bg-white border border-zinc-100 px-4 py-3 rounded-2xl rounded-tl-none shadow-sm flex gap-2 items-center">
                  <div className="w-1.5 h-1.5 bg-zinc-300 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-zinc-300 rounded-full animate-bounce delay-75"></div>
                  <div className="w-1.5 h-1.5 bg-zinc-300 rounded-full animate-bounce delay-150"></div>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 bg-white border-t border-zinc-100 flex gap-2">
            <input
              className="flex-1 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 text-[11px] font-bold focus:outline-none focus:border-black focus:ring-1 focus:ring-black/5 transition-all placeholder:text-zinc-400"
              placeholder="Ask about schedule, budget, or risks..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="p-3 bg-black text-white rounded-xl hover:scale-105 active:scale-95 disabled:opacity-50 transition-all shadow-lg"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
